import { Injectable, computed, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, fromEvent } from 'rxjs';

/** Raccourci annoncé dans l'aide (touche `?`). */
export interface ShortcutInfo {
  /** Touches, une entrée par touche : ['Ctrl', 'K'] (Ctrl devient ⌘ sur Mac). */
  keys: string[];
  /** Ce que fait le raccourci, en français. */
  label: string;
}

/** Ce que le clavier demande : ouvrir la recherche, ouvrir/fermer l'aide, ou rien. */
export type ShortcutAction = 'search' | 'help' | null;

/** Sous-ensemble d'un `KeyboardEvent` suffisant à classer un raccourci (testable sans DOM). */
export interface KeyLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/**
 * Classe une frappe. Ctrl/⌘ + K = recherche ; `?` = aide, seulement hors champ de saisie.
 * Toute autre combinaison (dont Ctrl+F, la recherche du navigateur) renvoie null : jamais
 * interceptée.
 */
export function classifyShortcut(event: KeyLike, inEditableField: boolean): ShortcutAction {
  const key = event.key.toLowerCase();
  if ((event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey && key === 'k') return 'search';
  if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey && !inEditableField) return 'help';
  return null;
}

/** La cible de la frappe est-elle un champ où l'on saisit du texte ? */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  const tag = el.tagName.toLowerCase();
  return el.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select';
}

const BASE_SHORTCUTS: ShortcutInfo[] = [
  { keys: ['Ctrl', 'K'], label: 'Rechercher dans la page, sinon demander à Koffi' },
  { keys: ['Échap'], label: 'Fermer le panneau latéral ou la fenêtre ouverte' },
  { keys: ['J', 'K'], label: 'Détail de tâche : tâche suivante / précédente' },
  { keys: ['?'], label: 'Afficher cette aide' },
];

/**
 * Raccourcis clavier globaux (MOKA-349).
 *
 * - `searchRequested$` : émet quand l'utilisateur tape Ctrl/⌘ + K. Une barre de recherche de
 *   module s'y abonne pour prendre le focus :
 *     `inject(ShortcutsService).searchRequested$.pipe(takeUntilDestroyed()).subscribe(() => this.input().nativeElement.focus());`
 *   Sans abonné, le repli `setSearchFallback` prend la main (MOKA-363 : ouvrir Koffi) ; s'il
 *   n'en veut pas, la frappe n'est pas interceptée (le navigateur garde son comportement).
 * - `?` (hors champ de saisie) ouvre l'aide ; `helpOpen` / `closeHelp()` la pilotent ; le composant
 *   `app-shortcuts-help` (monté dans `app.component`) l'affiche.
 * - `register(info)` ajoute une ligne à l'aide pour un raccourci propre à un écran (renvoie la
 *   fonction qui la retire — à appeler dans `ngOnDestroy`).
 * - Ctrl+F n'est JAMAIS intercepté. Échap est géré par `app.component` (panneau latéral, aide).
 */
@Injectable({ providedIn: 'root' })
export class ShortcutsService {
  private readonly search = new Subject<void>();
  private readonly extra = signal<ShortcutInfo[]>([]);
  private readonly open = signal(false);
  /** Repli de Ctrl/⌘ K quand aucun écran n'écoute : renvoie true s'il a pris la frappe. */
  private searchFallback: (() => boolean) | null = null;

  readonly searchRequested$: Observable<void> = this.search.asObservable();
  readonly helpOpen = this.open.asReadonly();
  /** Raccourcis listés dans l'aide : ceux de l'application puis ceux des écrans ouverts. */
  readonly shortcuts = computed(() => [...BASE_SHORTCUTS, ...this.extra()]);

  constructor() {
    if (typeof document === 'undefined') return;
    fromEvent<KeyboardEvent>(document, 'keydown')
      .pipe(takeUntilDestroyed())
      .subscribe(event => this.onKeydown(event));
  }

  register(info: ShortcutInfo): () => void {
    this.extra.update(list => [...list, info]);
    return () => this.extra.update(list => list.filter(i => i !== info));
  }

  /**
   * Repli de Ctrl/⌘ K (MOKA-363) : monté une fois par `app.component` pour ouvrir Koffi.
   * Annoncé dans l'aide, le raccourci ne restait jamais sans effet.
   */
  setSearchFallback(fallback: (() => boolean) | null): void {
    this.searchFallback = fallback;
  }

  openHelp(): void {
    this.open.set(true);
  }

  closeHelp(): void {
    this.open.set(false);
  }

  /** Sortie testable de la gestion d'une frappe : true si elle a été prise en charge. */
  onKeydown(event: KeyboardEvent): boolean {
    if (event.defaultPrevented) return false;
    const action = classifyShortcut(event, isEditableTarget(event.target));
    if (action === 'search') {
      if (this.search.observed) {
        event.preventDefault();
        this.search.next();
        return true;
      }
      if (this.searchFallback?.()) {
        event.preventDefault();
        return true;
      }
      return false;
    }
    if (action === 'help') {
      // Une modale (MatDialog…) ouverte garde la main.
      if (document.querySelector('.cdk-overlay-container .cdk-overlay-pane')) return false;
      event.preventDefault();
      this.open.update(v => !v);
      return true;
    }
    return false;
  }
}
